import { isImperial } from './units.js';

export const MANUAL_LENGTH_TEXT_LIMIT = 128;
/** Field inputs support point and comma grammars. The Arabic decimal mark is
 * point-equivalent; other locale marks fall back to point. */
const DEVICE_SEPARATOR_KEY = 'aviseraplanner.decimalSeparator';
/** The decimal mark this device uses for typed lengths: the operator's choice
 * under Units & scale, otherwise the device language's convention. */
export function deviceDecimalSeparator() {
  try { const saved = localStorage.getItem(DEVICE_SEPARATOR_KEY); if (saved === '.' || saved === ',') return saved; } catch {}
  return preferredLengthDecimalSeparator(typeof navigator === 'undefined' ? 'en-US' : navigator.language);
}
export function setDeviceDecimalSeparator(separator) {
  if (separator !== '.' && separator !== ',') throw new Error('Choose a decimal point or a decimal comma.');
  try { localStorage.setItem(DEVICE_SEPARATOR_KEY, separator); } catch {}
}
export function preferredLengthDecimalSeparator(locale) {
  try {
    return new Intl.NumberFormat(locale).formatToParts(1.1).find(part => part.type === 'decimal')?.value === ',' ? ',' : '.';
  } catch { return '.'; }
}
const fractions = {'¼':'1/4','½':'1/2','¾':'3/4','⅐':'1/7','⅑':'1/9','⅒':'1/10','⅓':'1/3','⅔':'2/3','⅕':'1/5','⅖':'2/5','⅗':'3/5','⅘':'4/5','⅙':'1/6','⅚':'5/6','⅛':'1/8','⅜':'3/8','⅝':'5/8','⅞':'7/8'};
const factors = {m:[1n,1n],cm:[1n,100n],mm:[1n,1000n],ft:[381n,1250n],in:[127n,5000n]};
const decimal = /^(?:\d+(?:\.\d*)?|\.\d+)$/;
const error = (code,message) => ({ok:false,code,error:message});

function quantity(value) {
  if (decimal.test(value)) {
    const [whole,part='']=value.split('.');
    return {n:BigInt((whole||'0')+part),d:10n**BigInt(part.length)};
  }
  const match=value.match(/^(?:(\d+) +)?(\d+)\s*\/\s*(\d+)$/);
  if (!match) return error('invalid-format','Enter a decimal length or a fraction with an explicit unit.');
  const whole=BigInt(match[1]||0),n=BigInt(match[2]),d=BigInt(match[3]);
  if (!d) return error('invalid-fraction','A fraction needs a denominator greater than zero.');
  if (match[1] !== undefined && n>=d) return error('invalid-fraction','Use a fraction smaller than one after a whole number, such as 8 1/2 in.');
  return {n:whole*d+n,d};
}

// Convert exact decimal/fraction/unit arithmetic once to the document's Number
// representation. No display rounding, parseFloat truncation or intermediate
// floating-point unit multiplication. The bounded input can contain at most
// 128 digits; 400 decimal places are ample even for its smallest fractions.
function numberFromRatio(n,d) {
  let value=String(n/d),remainder=n%d;
  if (remainder) {
    value+='.';
    for(let i=0;i<400&&remainder;i++) {remainder*=10n;value+=String(remainder/d);remainder%=d;}
  }
  return Number(value);
}

/** Strict operator-entered distance. Bare decimals use the Site's display unit;
 * explicit units override it. Device readings must bypass this parser. Results
 * retain the original trimmed text, while metres use normal double precision. */
export function parseManualLength(text,doc,options={}) {
  if (!options || typeof options!=='object' || Array.isArray(options)) return error('invalid-range','Use finite non-negative measurement limits.');
  const {min=0,max=10000}=options;
  if (!Number.isFinite(min)||!Number.isFinite(max)||min<0||max<min) return error('invalid-range','Use finite non-negative measurement limits.');
  if (Object.hasOwn(options,'decimalSeparator') && options.decimalSeparator!==',') return error('invalid-separator','Choose decimal point or decimal comma explicitly.');
  const comma=options.decimalSeparator===',';
  if (typeof text!=='string') return error('invalid-input','Enter the measurement as text.');
  if (text.length>MANUAL_LENGTH_TEXT_LIMIT) return error('too-long',`Keep a measurement within ${MANUAL_LENGTH_TEXT_LIMIT} characters.`);
  if (/[\u0000-\u001f\u007f]/.test(text)) return error('invalid-format','Enter one measurement on a single line.');
  const enteredText=text.trim();
  if (!enteredText) return error('empty','Enter a measurement.');
  const arabicPoint=enteredText.includes('\u066b');
  if (enteredText.includes('\u066c') || (comma ? enteredText.includes('.') || arabicPoint : enteredText.includes(',') || arabicPoint && enteredText.includes('.')))
    return error('ambiguous-separator',comma?'Use a decimal comma and no thousands separators, for example 1,25 m or 1250 mm. Change Number format under Units & scale to use a decimal point.':'Use a decimal point and no thousands separators, for example 1.25 m or 1250 mm. Change Number format under Units & scale to use a decimal comma.');
  let value=(comma?enteredText.replace(/,/g,'.'):enteredText).replace(/[\u0660-\u0669\u06f0-\u06f9]/g, digit =>
    String(digit.charCodeAt(0) - (digit.charCodeAt(0) <= 0x0669 ? 0x0660 : 0x06f0)))
    .replace(/\u066b/g,'.').replace(/[\u00a0\u202f\u2009]/g,' ').replace(/[′‘’]/g,"'").replace(/[″“”]/g,'"').toLowerCase();
  value=value.replace(/[¼½¾⅐⅑⅒⅓⅔⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞]/g,(character,offset,source)=>(/\d/.test(source[offset-1]||'')?' ':'')+fractions[character]);
  if (/[^0-9a-z .\/'"]/.test(value)) return error('invalid-format','Use a non-negative length with m, cm, mm, ft or in; arithmetic is not supported.');
  let result,enteredUnit,explicitUnit=true;
  const mixed=value.match(/^(\d+)\s*(?:ft|')\s*(.+?)\s*(?:in|")$/);
  if (mixed) {
    const inches=quantity(mixed[2].trim());
    if (inches.ok===false) return inches;
    if (inches.n>=12n*inches.d) return error('invalid-format','In a feet-and-inches measurement, enter fewer than 12 inches.');
    result={n:(BigInt(mixed[1])*12n*inches.d+inches.n)*127n,d:inches.d*5000n};
    enteredUnit='ft-in';
  } else {
    const explicit=value.match(/^(.+?)\s*(mm|cm|ft|in|m|'|")$/);
    let scalar;
    if (explicit) {
      enteredUnit=explicit[2]==="'"?'ft':explicit[2]==='"'?'in':explicit[2];
      scalar=quantity(explicit[1].trim());
      if (scalar.ok===false) return scalar;
    } else {
      if (!decimal.test(value)) return error('invalid-format','Use a decimal with the Site unit, or include m, cm, mm, ft or in. Write feet and inches as 5\' 8 1/2".');
      scalar=quantity(value);enteredUnit=isImperial(doc)?'ft':'m';explicitUnit=false;
    }
    result={n:scalar.n*factors[enteredUnit][0],d:scalar.d*factors[enteredUnit][1]};
  }
  const metres=numberFromRatio(result.n,result.d);
  if (!Number.isFinite(metres)||metres<min||metres>max) return error('out-of-range',`Enter a length between ${min} and ${max} metres. The measurement was not changed to fit.`);
  return {ok:true,metres,enteredText,enteredUnit,explicitUnit,...(comma?{decimalSeparator:','}:{})};
}

/** Explicitly allowlisted original entry, separate from native device payloads.
 * Absence of decimalSeparator permanently means the legacy point grammar. */
export function manualLengthMetadata(parsed) {
  if(!parsed?.ok)throw new Error('A valid manual length is required before retaining its original input.');
  return {enteredText:parsed.enteredText,enteredUnit:parsed.enteredUnit,explicitUnit:parsed.explicitUnit,...(parsed.decimalSeparator===','?{decimalSeparator:','}:{})};
}

/** Validate historical text against its recorded unit/format, never today's
 * Site preferences. Callers still enforce their own canonical metre binding. */
export function parseRecordedManualLength(raw,options={}) {
  if(!raw||typeof raw!=='object'||Array.isArray(raw)||!['enteredText','enteredUnit','explicitUnit'].every(k=>Object.hasOwn(raw,k))||Object.keys(raw).some(k=>!['enteredText','enteredUnit','explicitUnit','decimalSeparator'].includes(k))||Object.hasOwn(raw,'decimalSeparator')&&raw.decimalSeparator!==',')return error('invalid-evidence','The original manual length metadata is invalid.');
  if(!options||typeof options!=='object'||Array.isArray(options))return error('invalid-range','Use finite non-negative measurement limits.');
  const settings={...options};delete settings.decimalSeparator;
  const parsed=parseManualLength(raw.enteredText,{opt:{measurementSystem:raw.enteredUnit==='ft'?'imperial':'metric'}},{...settings,...(Object.hasOwn(raw,'decimalSeparator')?{decimalSeparator:raw.decimalSeparator}:{})});
  if(!parsed.ok)return parsed;
  if(parsed.enteredText!==raw.enteredText||parsed.enteredUnit!==raw.enteredUnit||parsed.explicitUnit!==raw.explicitUnit)return error('invalid-evidence','The original manual length does not match its recorded text or unit.');
  return parsed;
}

/** New explicit-format metadata opts legacy measurement records into strict
 * original-value binding. Unmarked legacy provenance retains its old rules. */
export function validateExplicitManualLength(raw,metres,{min=0,max=10000}={}) {
  if(!raw||typeof raw!=='object'||!Object.hasOwn(raw,'decimalSeparator'))return;
  const parsed=parseRecordedManualLength(raw,{min,max});
  if(!parsed.ok||!Number.isFinite(metres)||Math.abs(parsed.metres-metres)>1e-9)throw new Error('The original manual length format or recorded metre value is invalid.');
}
